import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '@surface-elements/shared/environments';
import { TemplateLayout } from '@surface-elements/reports/domain';

export interface TemplateSummary {
  _id: string;
  templateName: string;
  displayName: string;
  systemTemplate: boolean;
  hasDraft: boolean;
  hasPublished: boolean;
  draftUpdatedAt?: string;
  publishedUpdatedAt?: string;
}

export interface TemplateDetail {
  _id: string;
  templateName: string;
  displayName: string;
  systemTemplate: boolean;
  draft?: {
    layout: TemplateLayout;
    updatedAt?: string;
  };
  published?: {
    layout: TemplateLayout;
    updatedAt?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class ReportTemplateService {
  private http = inject(HttpClient);
  private baseUrl = `${environment.apiUrl}/reports/templates`;

  listTemplates(): Observable<TemplateSummary[]> {
    return this.http.get<TemplateSummary[]>(this.baseUrl);
  }

  createTemplate(
    templateName: string,
    displayName: string,
  ): Observable<{ _id: string; templateName: string; displayName: string }> {
    return this.http.post<{
      _id: string;
      templateName: string;
      displayName: string;
    }>(this.baseUrl, { templateName, displayName });
  }

  getTemplate(id: string): Observable<TemplateDetail> {
    return this.http.get<TemplateDetail>(`${this.baseUrl}/${id}`);
  }

  saveDraft(
    id: string,
    layout: TemplateLayout,
  ): Observable<{ message: string; draftUpdatedAt: string }> {
    return this.http.put<{ message: string; draftUpdatedAt: string }>(
      `${this.baseUrl}/${id}/draft`,
      { layout },
    );
  }

  publish(
    id: string,
  ): Observable<{ message: string; publishedUpdatedAt: string }> {
    return this.http.post<{ message: string; publishedUpdatedAt: string }>(
      `${this.baseUrl}/${id}/publish`,
      {},
    );
  }

  revert(id: string): Observable<{ message: string; draftUpdatedAt: string }> {
    return this.http.post<{ message: string; draftUpdatedAt: string }>(
      `${this.baseUrl}/${id}/revert`,
      {},
    );
  }

  preview(
    id: string,
    options: { orderId?: string; orderNumber?: number } = {},
  ): Observable<Blob> {
    let body: { orderId?: string; orderNumber?: number } = {};
    if (options.orderId) {
      body = { orderId: options.orderId };
    } else if (options.orderNumber != null) {
      body = { orderNumber: options.orderNumber };
    }
    return this.http.post(`${this.baseUrl}/${id}/preview`, body, {
      responseType: 'blob',
    });
  }
}
